/**
 * Связи и подзадачи.
 *
 * Иерархия и связи — разные вещи, и сервис держит их порознь: родитель лежит
 * полем самой задачи (`parent_id`), а «блокирует», «связана», «дублирует» —
 * отдельными строками. Экран показывает их рядом, потому что человек читает
 * их одинаково: «с чем ещё связана эта задача».
 */
import { issueApi } from '@/api'
import { toRow, type IssueRow } from '@/features/issues/api'

import type { V1IssueLink, V1LinkType } from '@/api/clients/tracker-issue'

/** Порядок в выпадашке: сначала то, что ставят чаще */
export const LINK_TYPES: V1LinkType[] = [
  'LINK_TYPE_BLOCKS',
  'LINK_TYPE_RELATES',
  'LINK_TYPE_DUPLICATES',
  'LINK_TYPE_CAUSES',
]

export interface IssueLink {
  id: string
  type: V1LinkType | ''
  targetKey: string
  targetTitle: string
  /** Задача на другом конце связи: строка читается наоборот */
  inbound: boolean
}

const toLink = (link: V1IssueLink): IssueLink => ({
  id: link.id ?? '',
  type: link.linkType ?? '',
  targetKey: link.targetKey ?? '',
  targetTitle: link.targetTitle ?? '',
  inbound: Boolean(link.inbound),
})

export const listLinks = async (issueId: string): Promise<IssueLink[]> => {
  const { data } = await issueApi.issueServiceListIssueLinks(issueId)

  return (data.links ?? []).map(toLink)
}

export const createLink = async (
  issueId: string,
  targetIssueId: string,
  linkType: V1LinkType,
): Promise<void> => {
  await issueApi.issueServiceCreateIssueLink(issueId, { targetIssueId, linkType })
}

export const deleteLink = async (id: string): Promise<void> => {
  await issueApi.issueServiceDeleteIssueLink(id)
}

/**
 * Подзадачи — это задачи с этим родителем, и берёт их обычный листинг: своей
 * ручки «дети такой-то задачи» у сервиса нет, а фильтр по родителю есть.
 */
export const listChildren = async (projectId: string, parentId: string): Promise<IssueRow[]> => {
  const { data } = await issueApi.issueServiceListIssues(
    projectId,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    parentId,
    undefined,
    undefined,
    undefined, // filterProjectIds
    undefined, // filterStatusCategories
    undefined, // filterDueAfter
    'key',
    false,
    '1',
    // Сотня подзадач у одной задачи — уже не иерархия, а отдельный проект:
    // страницы здесь были бы дороже, чем пользы от них.
    '100',
  )

  return (data.issues ?? []).map(toRow)
}
